<template>
    <a
        :class="'podium-card'"
        :aria-label="partner.partner_name"
        :href="clickOutUrl(partner, index)"
        @clicked="clickoutEvent(partner, index)"
        target="_blank"
        :style="{
            '--podium-score-text-color': scoreTextColor,
            '--podium-score-bg-color': scoreBgColor,
            '--podium-score-number-text-color': scoreNumberTextColor,
            '--info-text-color': textColor,
        }"
    >
        <div
            class="container"
            :style="{ borderColor: index === 0 ? borderColor : '#eee' }"
        >
            <div>
                <div
                    class="flag-zeta"
                    v-if="index === 0"
                    :style="{
                        '--flag-bg-color': flagColor,
                        '--flag-text-color': flagTextColor,
                    }"
                >
                    <div class="flag-zeta__inner">
                        {{ $t('Most Popular') }}
                    </div>
                </div>
                <BaseImage
                    loadLazy
                    class="image"
                    :alt="partner.partner_name"
                    :img-source="partner.logo_image"
                    width="200px"
                    height="100px"
                    :style="{ height: 'auto', width: '100px' }"
                    quality="80"
                />
                <div class="podium-rating">
                    <div class="podium-score">
                        {{ formatScore(partner.score) }}
                    </div>
                    <div class="podium-reviews">
                        <div class="podium-review-stars">
                            <Rating v-bind="ratingProps"></Rating>
                        </div>
                    </div>
                </div>
            </div>
            <div class="right-section">
                <div class="info">
                    {{ partner.marketing_properties.podium_pro_short_discount }}
                </div>
                <CtaButton
                    is-active
                    class="button"
                    :aria-label="partner.partner_name"
                    :clickout-link="clickOutUrl(partner, index)"
                    v-bind="buttonProps"
                    @clicked="clickoutEvent(partner, index)"
                >
                    {{ $t('Visit Site') }}
                </CtaButton>
            </div>
        </div>
    </a>
</template>
<script>
import { COT_PODIUM, scoreTypes } from '@/constants';
import { analyticsMixin, clickOutMixin } from '@/mixins';
import { PodiumProProps } from '@molecules/PodiumPro/PodiumPro.props';
export default {
    name: 'PodiumCard',
    mixins: [analyticsMixin, clickOutMixin],
    props: {
        ...PodiumProProps,
        partner: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
        },
    },
    computed: {
        ratingProps() {
            return {
                height: this.ratingIconSize,
                width: this.ratingIconSize,
                activeColor: this.ratingIconColor,
                inactiveColor: this.ratingIconInactiveColor,
                activeIcon: this.ratingIconType,
                rating: this.partner.score,
            };
        },
        scoreStyles() {
            return {
                fontSize: this.scoreTextSize,
            };
        },
        buttonProps() {
            return {
                shape: 'square',
                buttonType: this.buttonType,
                fill: this.buttonColor,
                textColor: this.buttonTextColor,
            };
        },
    },
    methods: {
        formatScore(score) {
            if (
                Boolean(score) &&
                score.toString().length < 2 &&
                this.scoreType === scoreTypes.number
            ) {
                score = score + '.0';
                return score;
            }
            return score;
        },
        clickoutEvent(partner, index) {
            this.clickout(
                this.categoryState.category.name,
                partner.clicked_partner_name,
                index,
                'podium_pro'
            );
        },
        clickOutUrl(partner, index) {
            return this.goto(partner.cp_id, index + 1, COT_PODIUM);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';

.podium-card {
    position: relative;
    display: flex;
    height: fit-content;

    &:hover .container {
        transform: translateY(-12px);

        .info {
            text-decoration: underline;
        }
    }

    &:first-of-type .container {
        .image {
            max-height: 50px;
        }
    }

    .container {
        display: grid;
        grid-template-columns: 1fr 1.4fr;
        gap: 10px 20px;
        border: 1.5px solid transparent;
        box-shadow: 0 -1px 2px rgba(24, 28, 71, 0.04),
            0 2px 4px rgba(24, 28, 71, 0.1);
        border-radius: 4px;
        width: 100%;
        padding: 12px;
        background: #fff;
        align-items: center;
        transition: transform 0.3s ease-out;
        will-change: transform;
    }

    .flag-zeta {
        display: flex;

        &__inner {
            margin-bottom: 8px;
            color: var(--flag-text-color, #fff);
            font-size: 12px;
            font-weight: 700;
            line-height: 15px;
            padding: 2px 16px;
            position: relative;
            z-index: 0;
            display: flex;

            &::before {
                content: '';
                background: var(--flag-bg-color);
                height: 100%;
                left: 0;
                position: absolute;
                top: 0;
                transform: skewX(-30deg);
                width: 100%;
                z-index: -1;
            }
        }
    }

    .image {
        grid-column: 1;
        grid-row: 1;
        object-fit: contain;
        max-height: 40px;
    }

    .podium-score {
        font-size: 16px;
        line-height: 1.5;
        font-weight: 700;
        color: var(--podium-score-number-text-color);
        background-color: var(--podium-score-bg-color);
        border: 1px solid var(--podium-score-bg-color);
        max-width: 50px;
        text-align: center;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 3px 0;
    }

    .podium-reviews {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .podium-review-title {
        text-transform: uppercase;
        font-size: 10px;
        font-weight: 700;
        color: var(--partner-score-text-color);

        @include mq('tablet') {
            font-size: 11px;
        }
    }

    .podium-rating {
        width: auto;
        border: 1px solid #eee;
        display: flex;
        align-items: center;
        padding-right: 0 10px;
        margin-top: 10px;
    }

    .info {
        grid-column: 2;
        grid-row: 1;
        font-size: 14px;
        font-weight: bold;
        line-height: 1.4;
        color: var(--info-text-color);
        line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
    }

    .button {
        grid-column: 2;
        grid-row: 2;
        margin-top: 10px;
    }
}

.podium-card.dark {
    .container {
        background: #1a1a1a;
        border-color: #1a1a1a;
    }

    .info,
    .score {
        color: #fff;
    }
}

.right-section {
    display: flex;
    flex-direction: column;
}
</style>
